import React, { useState, useEffect, useMemo } from 'react';
import IconText from './icons/IconText';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import { drillingCostsService } from '../services/drillingCostsService';
import {
  buildPeriodSelectOptions,
  formatDateLocal,
  getDisplayPeriodRange
} from '../utils/invoicingPeriods';
import './DrillingCosts.css';

const DrillingCosts = ({ user }) => {
  // Data state
  const [outliersData, setOutliersData] = useState([]);
  const [standbyData, setStandbyData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const [costLedgerData, setCostLedgerData] = useState([]);
  const [coringActivityData, setCoringActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('outliers');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Outliers filters - Updated to match new schema
  const [activityFilter, setActivityFilter] = useState('all'); // Replaces both code+desc
  const [activityTypeFilter, setActivityTypeFilter] = useState('all'); // New field
  const [holeNameFilter, setHoleNameFilter] = useState('all');
  const [outlierFlagFilter, setOutlierFlagFilter] = useState('all');

  // Standby filters - Updated to match new schema
  const [rigNameFilter, setRigNameFilter] = useState('all');
  const [standbyHoleFilter, setStandbyHoleFilter] = useState('all');
  const [standbyActivityFilter, setStandbyActivityFilter] = useState('all'); // Replaces both code+desc
  const [standbyActivityTypeFilter, setStandbyActivityTypeFilter] = useState('all'); // New field
  
  // Invoice filters
  const [periodFilter, setPeriodFilter] = useState('all');
  const [costCategoryFilter, setCostCategoryFilter] = useState('all');
  
  // Timeline filters - Updated to match new schema
  const [timelineGroupBy, setTimelineGroupBy] = useState('rig_name');
  const [timelineHoleFilter, setTimelineHoleFilter] = useState('all');
  const [timelineActivityFilter, setTimelineActivityFilter] = useState([]); // Multi-select: array of selected activities
  const [timelineActivityTypeFilter, setTimelineActivityTypeFilter] = useState('all'); // New field
  const [timelineCostCategoryFilter, setTimelineCostCategoryFilter] = useState([]); // Multi-select: array of selected cost categories
  const [targetCostPerMeter, setTargetCostPerMeter] = useState(150); // Default target
  
  // Selected outlier for visualization
  const [selectedOutlier, setSelectedOutlier] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Load timeline data once when timeline tab becomes active
  useEffect(() => {
    if (activeTab === 'timeline' && costLedgerData.length === 0) {
      console.log('🔄 [Timeline] Triggering initial data load');
      loadTimelineData();
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    console.log('🔄 [DrillingCosts Component] Starting loadDashboardData...');
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('📞 [DrillingCosts Component] Calling drillingCostsService.getDashboardData()...');
      const data = await drillingCostsService.getDashboardData();
      
      console.log('📦 [DrillingCosts Component] Received data:', {
        outliers: data.outliers?.length || 0,
        standby: data.standby?.length || 0,
        invoices: data.invoices?.length || 0,
        partialFailures: data.partialFailures
      });
      
      setOutliersData(data.outliers || []);
      setStandbyData(data.standby || []);
      setInvoiceData(data.invoices || []);
      
      // Show warning if some endpoints failed but not all
      if (data.partialFailures && data.partialFailures.length > 0) {
        const warningMsg = `Some endpoints failed to load: ${data.partialFailures.join(', ')}. Other data is still available.`;
        console.warn('⚠️ [DrillingCosts Component]', warningMsg);
        // Could set a warning state here if needed
      }
      
      console.log('✅ [DrillingCosts Component] Data successfully loaded and set in state');
    } catch (err) {
      console.error('❌ [DrillingCosts Component] Error loading drilling costs data:', err);
      console.error('❌ [DrillingCosts Component] Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      const errorMsg = err.message || 'Failed to load drilling costs data';
      setError(errorMsg);
      
      // Set empty arrays to prevent undefined errors
      setOutliersData([]);
      setStandbyData([]);
      setInvoiceData([]);
    } finally {
      setLoading(false);
      console.log('🏁 [DrillingCosts Component] loadDashboardData completed');
    }
  };

  const loadTimelineData = async () => {
    console.log('🔄 [Timeline] Loading timeline data (ALL TIME)...');
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch ALL data - no date filtering, high limit for full history
      const [costLedger, coringActivity] = await Promise.all([
        drillingCostsService.getCostLedger({ limit: 100000 }),
        drillingCostsService.getCoringActivity({ limit: 100000 })
      ]);
      
      console.log('📦 [Timeline] Received data:', {
        costLedger: costLedger.data?.length || 0,
        coringActivity: coringActivity.data?.length || 0,
        note: 'Fetching with limit=100000 to get full history'
      });
      
      // Log sample data for debugging
      if (costLedger.data && costLedger.data.length > 0) {
        console.log('💰 Sample cost ledger record:', costLedger.data[0]);
        
        // Show date range in the data
        const costDates = costLedger.data.map(d => d.activity_date).filter(Boolean);
        const uniqueCostDates = [...new Set(costDates)].sort();
        console.log('💰 Cost ledger date range:', {
          first: uniqueCostDates[0],
          last: uniqueCostDates[uniqueCostDates.length - 1],
          uniqueDates: uniqueCostDates.length,
          sample: uniqueCostDates.slice(0, 5)
        });
      }
      if (coringActivity.data && coringActivity.data.length > 0) {
        console.log('📏 Sample coring activity record:', coringActivity.data[0]);
        
        // Show date range in the data
        const coringDates = coringActivity.data.map(d => d.report_date).filter(Boolean);
        const uniqueCoringDates = [...new Set(coringDates)].sort();
        console.log('📏 Coring activity date range:', {
          first: uniqueCoringDates[0],
          last: uniqueCoringDates[uniqueCoringDates.length - 1],
          uniqueDates: uniqueCoringDates.length,
          sample: uniqueCoringDates.slice(0, 5)
        });
      } else {
        console.warn('⚠️ No coring activity data received!');
      }
      
      setCostLedgerData(costLedger.data || []);
      setCoringActivityData(coringActivity.data || []);
      
    } catch (err) {
      console.error('❌ [Timeline] Error loading timeline data:', err);
      setError(err.message || 'Failed to load timeline data');
      setCostLedgerData([]);
      setCoringActivityData([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const outliersFilterOptions = useMemo(() => ({
    activities: drillingCostsService.getUniqueValues(outliersData, 'activity'),
    activityTypes: drillingCostsService.getUniqueValues(outliersData, 'activity_type'),
    holeNames: drillingCostsService.getUniqueValues(outliersData, 'hole_name'),
    outlierFlags: drillingCostsService.getUniqueValues(outliersData, 'outlier_flag')
  }), [outliersData]);

  const standbyFilterOptions = useMemo(() => ({
    rigNames: drillingCostsService.getUniqueValues(standbyData, 'rig_name'),
    holeNames: drillingCostsService.getUniqueValues(standbyData, 'hole_name'),
    activities: drillingCostsService.getUniqueValues(standbyData, 'activity'),
    activityTypes: drillingCostsService.getUniqueValues(standbyData, 'activity_type')
  }), [standbyData]);

  const invoiceFilterOptions = useMemo(() => ({
    periods: drillingCostsService.getUniqueValues(invoiceData, 'period_id'),
    costCategories: drillingCostsService.getUniqueValues(invoiceData, 'cost_category')
  }), [invoiceData]);

  const periodDateRangeOptionsByYear = useMemo(() => {
    const opts = buildPeriodSelectOptions(14);
    const byYear = {};
    opts.forEach((o) => {
      const year = o.value.slice(0, 4);
      if (!byYear[year]) byYear[year] = [];
      byYear[year].push(o);
    });
    return Object.entries(byYear).sort((a, b) => b[0].localeCompare(a[0]));
  }, []);

  // Filter outliers data
  const filteredOutliers = useMemo(() => {
    let filtered = [...outliersData];

    // Apply search
    filtered = drillingCostsService.filterData(filtered, searchTerm);

    // Apply date filter
    filtered = drillingCostsService.filterByDateRange(filtered, dateFilter, customStartDate, customEndDate);

    // Apply column filters - Updated to use new schema fields
    if (activityFilter !== 'all') {
      filtered = filtered.filter(item => item.activity === activityFilter);
    }
    if (activityTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.activity_type === activityTypeFilter);
    }
    if (holeNameFilter !== 'all') {
      filtered = filtered.filter(item => item.hole_name === holeNameFilter);
    }
    if (outlierFlagFilter !== 'all') {
      filtered = filtered.filter(item => item.outlier_flag === outlierFlagFilter);
    }
    
    return filtered;
  }, [outliersData, searchTerm, dateFilter, customStartDate, customEndDate, activityFilter, activityTypeFilter, holeNameFilter, outlierFlagFilter]);

  // Filter standby data
  const filteredStandby = useMemo(() => {
    let filtered = [...standbyData];

    // Apply search
    filtered = drillingCostsService.filterData(filtered, searchTerm);

    // Apply date filter
    filtered = drillingCostsService.filterByDateRange(filtered, dateFilter, customStartDate, customEndDate);
    
    // Apply column filters - Updated to use new schema fields
    if (rigNameFilter !== 'all') {
      filtered = filtered.filter(item => item.rig_name === rigNameFilter);
    }
    if (standbyHoleFilter !== 'all') {
      filtered = filtered.filter(item => item.hole_name === standbyHoleFilter);
    }
    if (standbyActivityFilter !== 'all') {
      filtered = filtered.filter(item => item.activity === standbyActivityFilter);
    }
    if (standbyActivityTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.activity_type === standbyActivityTypeFilter);
    }
    
    return filtered;
  }, [standbyData, searchTerm, dateFilter, customStartDate, customEndDate, rigNameFilter, standbyHoleFilter, standbyActivityFilter, standbyActivityTypeFilter]);

  // Filter invoice data
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoiceData];
    
    // Apply period filter
    if (periodFilter !== 'all') {
      filtered = filtered.filter(item => item.period_id === periodFilter);
    }
    
    // Apply cost category filter
    if (costCategoryFilter !== 'all') {
      filtered = filtered.filter(item => item.cost_category === costCategoryFilter);
    }
    
    return filtered;
  }, [invoiceData, periodFilter, costCategoryFilter]);

  // Group invoice data by period for display
  const groupedInvoices = useMemo(() => 
    drillingCostsService.groupByPeriod(filteredInvoices), 
    [filteredInvoices]
  );

  // Calculate statistics
  const outlierStats = useMemo(() => 
    drillingCostsService.calculateOutlierStats(filteredOutliers), 
    [filteredOutliers]
  );

  const standbyStats = useMemo(() => 
    drillingCostsService.calculateStandbyStats(filteredStandby), 
    [filteredStandby]
  );

  const invoiceStats = useMemo(() => 
    drillingCostsService.calculateInvoiceStats(filteredInvoices), 
    [filteredInvoices]
  );

  // Filter timeline data - smart filtering across datasets
  const filteredTimelineCosts = useMemo(() => {
    console.log('🔄 Filtering cost ledger...', {
      total: costLedgerData.length,
      holeFilter: timelineHoleFilter,
      selectedActivities: timelineActivityFilter,
      activityType: timelineActivityTypeFilter,
      selectedCostCategories: timelineCostCategoryFilter
    });
    
    let filtered = costLedgerData;
    
    // Filter by hole name (matches both datasets)
    if (timelineHoleFilter !== 'all') {
      filtered = filtered.filter(item => item.hole_name === timelineHoleFilter);
    }
    
    // Filter by activity (ONLY costs) - Multi-select: include if in selected array
    // NOTE: cost_ledger uses 'item_description' field (old schema)
    if (timelineActivityFilter.length > 0) {
      filtered = filtered.filter(item => timelineActivityFilter.includes(item.item_description));
    }
    
    // Filter by activity type (ONLY costs)
    // NOTE: cost_ledger uses 'activity_code' field (old schema)
    if (timelineActivityTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.activity_code === timelineActivityTypeFilter);
    }
    
    // Filter by cost category (ONLY costs) - Multi-select: include if in selected array
    if (timelineCostCategoryFilter.length > 0) {
      filtered = filtered.filter(item => timelineCostCategoryFilter.includes(item.cost_category));
    }
    
    console.log('✅ Filtered cost ledger:', filtered.length);
    return filtered;
  }, [costLedgerData, timelineHoleFilter, timelineActivityFilter, timelineActivityTypeFilter, timelineCostCategoryFilter]);

  const filteredTimelineCoring = useMemo(() => {
    console.log('🔄 Filtering coring activity...', {
      total: coringActivityData.length,
      holeFilter: timelineHoleFilter
    });
    
    let filtered = coringActivityData;
    
    // Only filter by hole name (NOT by activity filters)
    if (timelineHoleFilter !== 'all') {
      filtered = filtered.filter(item => item.hole_name === timelineHoleFilter);
    }
    
    console.log('✅ Filtered coring activity:', filtered.length);
    return filtered;
  }, [coringActivityData, timelineHoleFilter]);

  const timelineData = useMemo(() => {
    console.log('🔄 [Timeline useMemo] Recalculating timeline data...');
    console.log('📊 Filtered data counts:', {
      costs: filteredTimelineCosts.length,
      coring: filteredTimelineCoring.length,
      groupBy: timelineGroupBy
    });
    
    const result = drillingCostsService.aggregateTimelineData(
      filteredTimelineCosts,
      filteredTimelineCoring,
      timelineGroupBy
    );
    
    console.log('📈 Timeline data sample:', result.slice(0, 3));
    return result;
  }, [filteredTimelineCosts, filteredTimelineCoring, timelineGroupBy]);

  const timelineStats = useMemo(() => 
    drillingCostsService.calculateTimelineStats(timelineData), 
    [timelineData]
  );

  // Get unique groups for chart coloring
  const timelineGroups = useMemo(() => {
    const groups = new Set();
    timelineData.forEach(day => {
      Object.keys(day).forEach(key => {
        if (key !== 'date' && key !== 'totalCost' && key !== 'totalMeters' && key !== 'costPerMeter' && key.endsWith('_cost')) {
          groups.add(key.replace('_cost', ''));
        }
      });
    });
    return Array.from(groups).sort();
  }, [timelineData]);

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setDateFilter('all');
    if (activeTab === 'outliers') {
      setActivityFilter('all');
      setActivityTypeFilter('all');
      setHoleNameFilter('all');
      setOutlierFlagFilter('all');
    } else if (activeTab === 'standby') {
      setRigNameFilter('all');
      setStandbyHoleFilter('all');
      setStandbyActivityFilter('all');
      setStandbyActivityTypeFilter('all');
    } else if (activeTab === 'invoices') {
      setPeriodFilter('all');
      setCostCategoryFilter('all');
    } else if (activeTab === 'timeline') {
      setTimelineGroupBy('rig_name');
      setTimelineHoleFilter('all');
      setTimelineActivityFilter([]); // Clear all selected activities
      setTimelineActivityTypeFilter('all');
      setTimelineCostCategoryFilter([]); // Clear all selected cost categories
      setTargetCostPerMeter(150);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => formatDateLocal(dateStr);

  return (
    <Layout user={user} currentRoute="/drilling-costs">
      <div className="drilling-costs">
        <div className="drilling-costs-header">
          <IconText icon={ICON.appDrillingCosts} as="h1">Drilling Costs Dashboard</IconText>
          <p>Track activity outliers and standby time to optimize drilling efficiency</p>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading drilling costs data...</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <IconText icon={ICON.uiError} as="h3">Error Loading Data</IconText>
            <p>{error}</p>
            <div className="error-details">
              <p className="error-hint">Check the browser console (F12) for detailed error logs.</p>
              <p className="error-hint">Common issues:</p>
              <ul className="error-list">
                <li>Not a member of the Drilling-Costs-Users group (ID: b09f7b5b-7cb8-43bf-b20f-e1b4f2bd5391)</li>
                <li>Azure Functions not deployed or not running</li>
                <li>Database connection issues</li>
                <li>Network/CORS configuration</li>
              </ul>
            </div>
            <Button onClick={loadDashboardData}>Retry</Button>
          </Card>
        )}

        {!loading && !error && (
          <>
            {/* Tabs */}
            <Card className="tabs-card">
              <div className="tabs-container">
                <button 
                  className={`tab-button ${activeTab === 'outliers' ? 'active' : ''}`}
                  onClick={() => setActiveTab('outliers')}
                >
                  <IconText icon={ICON.uiWarning} iconSize={14}>Activity Outliers</IconText>
                  <span className="tab-badge">{filteredOutliers.length}</span>
                </button>
                <button 
                  className={`tab-button ${activeTab === 'standby' ? 'active' : ''}`}
                  onClick={() => setActiveTab('standby')}
                >
                  <IconText icon={ICON.uiPause} iconSize={14}>Standby Detail</IconText>
                  <span className="tab-badge">{filteredStandby.length}</span>
                </button>
                <button 
                  className={`tab-button ${activeTab === 'invoices' ? 'active' : ''}`}
                  onClick={() => setActiveTab('invoices')}
                >
                  <IconText icon={ICON.uiDocument} iconSize={14}>Invoice Reconciliation</IconText>
                  <span className="tab-badge">{groupedInvoices.length}</span>
                </button>
                <button 
                  className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}
                  onClick={() => setActiveTab('timeline')}
                >
                  <IconText icon={ICON.uiChartAnalytics} iconSize={14}>Timeline</IconText>
                  <span className="tab-badge">{timelineData.length}</span>
                </button>
              </div>
            </Card>

            {/* Summary Stats */}
            {activeTab === 'outliers' ? (
              <Card className="summary-card outliers-summary">
                <IconText icon={ICON.uiChartAnalytics} as="h2">Outliers Overview</IconText>
                <div className="summary-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Records</span>
                    <span className="stat-value">{outlierStats.totalRecords}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Extreme Outliers</span>
                    <span className="stat-value danger">{outlierStats.extremeOutliers}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Excess Cost</span>
                    <span className="stat-value">{formatCurrency(outlierStats.totalExcessCost)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Z-Score</span>
                    <span className="stat-value">{outlierStats.avgZScore}σ</span>
                  </div>
                </div>
              </Card>
            ) : activeTab === 'standby' ? (
              <Card className="summary-card standby-summary">
                <IconText icon={ICON.uiChartAnalytics} as="h2">Standby Overview</IconText>
                <div className="summary-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Records</span>
                    <span className="stat-value">{standbyStats.totalRecords}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Standby Hours</span>
                    <span className="stat-value">{standbyStats.totalStandbyHours}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Standby Cost</span>
                    <span className="stat-value">{formatCurrency(standbyStats.totalStandbyCost)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Hours/Record</span>
                    <span className="stat-value">{standbyStats.avgStandbyHours}</span>
                  </div>
                </div>
              </Card>
            ) : activeTab === 'invoices' ? (
              <Card className="summary-card invoice-summary">
                <IconText icon={ICON.uiChartAnalytics} as="h2">Invoice Overview</IconText>
                <div className="summary-grid">
                  <div className="stat-item">
                    <span className="stat-label">Categories</span>
                    <span className="stat-value">{invoiceStats.totalCategories}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Line Items</span>
                    <span className="stat-value">{invoiceStats.totalLineItems.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Grand Total</span>
                    <span className="stat-value">{formatCurrency(invoiceStats.grandTotal)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Category Total</span>
                    <span className="stat-value">{formatCurrency(invoiceStats.avgCategoryTotal)}</span>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="summary-card timeline-summary">
                <IconText icon={ICON.uiChartAnalytics} as="h2">Timeline Overview</IconText>
                {(timelineActivityFilter.length > 0 || timelineCostCategoryFilter.length > 0) && (
                  <div className="filter-indicator">
                    {timelineActivityFilter.length > 0 && (
                      <>
                        <span className="filter-indicator-label">Filtered by Activity Descriptions:</span>
                        <div className="filter-indicator-tags">
                          {timelineActivityFilter.map(activity => (
                            <span key={activity} className="filter-tag">{activity}</span>
                          ))}
                        </div>
                      </>
                    )}
                    {timelineCostCategoryFilter.length > 0 && (
                      <>
                        <span className="filter-indicator-label" style={{ marginTop: timelineActivityFilter.length > 0 ? '0.5rem' : '0' }}>
                          Filtered by Cost Categories:
                        </span>
                        <div className="filter-indicator-tags">
                          {timelineCostCategoryFilter.map(category => (
                            <span key={category} className="filter-tag">{category}</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="summary-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Cost</span>
                    <span className="stat-value">{formatCurrency(timelineStats.totalCost)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Meters</span>
                    <span className="stat-value">{timelineStats.totalMeters}m</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Cost/Meter</span>
                    <span className="stat-value stat-highlight">${timelineStats.avgCostPerMeter}/m</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Days with Data</span>
                    <span className="stat-value">{timelineStats.totalShifts}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Controls and Filters */}
            <Card className="controls-card">
              <div className="controls-header">
                <IconText icon={ICON.uiFilter} as="h3">Filters</IconText>
                <div className="controls-actions">
                  <Button onClick={resetFilters} variant="secondary" size="small">
                    Clear Filters
                  </Button>
                  <Button onClick={loadDashboardData} variant="secondary" size="small">
                    <IconText icon={ICON.uiRefresh} iconSize={14}>Refresh</IconText>
                  </Button>
                </div>
              </div>

              {/* Search and Date Filter (for outliers and standby only) */}
              {activeTab !== 'invoices' && activeTab !== 'timeline' && (
                <>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Search</label>
                      <input
                        type="text"
                        placeholder="Search all fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                  </div>
                  
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Date Range</label>
                      <select 
                        value={dateFilter} 
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All Time</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                        {periodDateRangeOptionsByYear.map(([year, options]) => (
                          <optgroup key={year} label={`${year} Periods`}>
                            {options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                        <option value="custom">Custom Date Range...</option>
                      </select>
                    </div>
                  </div>

                  {/* Custom Date Range */}
                  {dateFilter === 'custom' && (
                    <div className="filter-row">
                      <div className="filter-group">
                        <label>Start Date</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="filter-input"
                        />
                      </div>
                      <div className="filter-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="filter-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Invoice-specific filters */}
              {activeTab === 'invoices' && (
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Period</label>
                    <select 
                      value={periodFilter} 
                      onChange={(e) => setPeriodFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Periods</option>
                      {invoiceFilterOptions.periods.map(period => (
                        <option key={period} value={period}>{period}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Cost Category</label>
                    <select 
                      value={costCategoryFilter} 
                      onChange={(e) => setCostCategoryFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Categories</option>
                      {invoiceFilterOptions.costCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Timeline-specific filters */}
              {activeTab === 'timeline' && (
                <>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Hole Name</label>
                      <select 
                        value={timelineHoleFilter} 
                        onChange={(e) => setTimelineHoleFilter(e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All Holes</option>
                        {drillingCostsService.getUniqueValues(costLedgerData, 'hole_name')
                          .filter(h => h)
                          .sort()
                          .map(hole => (
                            <option key={hole} value={hole}>{hole}</option>
                          ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <label>Activity Type</label>
                      <select 
                        value={timelineActivityTypeFilter} 
                        onChange={(e) => setTimelineActivityTypeFilter(e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All Types</option>
                        {drillingCostsService.getUniqueValues(costLedgerData, 'activity_code')
                          .filter(a => a)
                          .sort()
                          .map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="filter-row">
                    <div className="filter-group filter-checkbox-group">
                      <label>
                        Activity Description Filter 
                        <span className="filter-count">
                          ({timelineActivityFilter.length > 0 ? `${timelineActivityFilter.length} selected` : 'All'})
                        </span>
                      </label>
                      <div className="checkbox-list">
                        {(() => {
                          const activities = drillingCostsService.getUniqueValues(costLedgerData, 'item_description')
                            .filter(d => d)
                            .sort();
                          
                          if (activities.length === 0) {
                            return (
                              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                                No activities found. Load timeline data first.
                              </div>
                            );
                          }
                          
                          return activities.map(activity => (
                            <label key={activity} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={timelineActivityFilter.includes(activity)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTimelineActivityFilter([...timelineActivityFilter, activity]);
                                  } else {
                                    setTimelineActivityFilter(timelineActivityFilter.filter(a => a !== activity));
                                  }
                                }}
                              />
                              <span className="checkbox-label">{activity}</span>
                            </label>
                          ));
                        })()}
                      </div>
                      {timelineActivityFilter.length > 0 && (
                        <button 
                          className="clear-selection-btn"
                          onClick={() => setTimelineActivityFilter([])}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="filter-row">
                    <div className="filter-group filter-checkbox-group">
                      <label>
                        Cost Category Filter 
                        <span className="filter-count">
                          ({timelineCostCategoryFilter.length > 0 ? `${timelineCostCategoryFilter.length} selected` : 'All'})
                        </span>
                      </label>
                      <div className="checkbox-list">
                        {(() => {
                          const categories = drillingCostsService.getUniqueValues(costLedgerData, 'cost_category')
                            .filter(d => d)
                            .sort();
                          
                          if (categories.length === 0) {
                            return (
                              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                                No cost categories found. Load timeline data first.
                              </div>
                            );
                          }
                          
                          return categories.map(category => (
                            <label key={category} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={timelineCostCategoryFilter.includes(category)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTimelineCostCategoryFilter([...timelineCostCategoryFilter, category]);
                                  } else {
                                    setTimelineCostCategoryFilter(timelineCostCategoryFilter.filter(c => c !== category));
                                  }
                                }}
                              />
                              <span className="checkbox-label">{category}</span>
                            </label>
                          ));
                        })()}
                      </div>
                      {timelineCostCategoryFilter.length > 0 && (
                        <button 
                          className="clear-selection-btn"
                          onClick={() => setTimelineCostCategoryFilter([])}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Group By</label>
                      <select 
                        value={timelineGroupBy} 
                        onChange={(e) => setTimelineGroupBy(e.target.value)}
                        className="filter-select"
                      >
                        <option value="rig_name">Rig</option>
                        <option value="shift">Shift</option>
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label>Target Cost/Meter: ${targetCostPerMeter}/m</label>
                      <input
                        type="range"
                        value={targetCostPerMeter}
                        onChange={(e) => setTargetCostPerMeter(parseFloat(e.target.value))}
                        className="filter-slider"
                        min="100"
                        max="500"
                        step="5"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Tab-specific filters */}
              {activeTab === 'outliers' ? (
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Activity Type</label>
                    <select 
                      value={activityTypeFilter} 
                      onChange={(e) => setActivityTypeFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Types</option>
                      {outliersFilterOptions.activityTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Activity</label>
                    <select 
                      value={activityFilter} 
                      onChange={(e) => setActivityFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Activities</option>
                      {outliersFilterOptions.activities.map(activity => (
                        <option key={activity} value={activity}>{activity}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Hole Name</label>
                    <select 
                      value={holeNameFilter} 
                      onChange={(e) => setHoleNameFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All</option>
                      {outliersFilterOptions.holeNames.map(hole => (
                        <option key={hole} value={hole}>{hole}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Outlier Flag</label>
                    <select 
                      value={outlierFlagFilter} 
                      onChange={(e) => setOutlierFlagFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All</option>
                      {outliersFilterOptions.outlierFlags.map(flag => (
                        <option key={flag} value={flag}>{flag}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Rig Name</label>
                    <select 
                      value={rigNameFilter} 
                      onChange={(e) => setRigNameFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All</option>
                      {standbyFilterOptions.rigNames.map(rig => (
                        <option key={rig} value={rig}>{rig}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Hole Name</label>
                    <select 
                      value={standbyHoleFilter} 
                      onChange={(e) => setStandbyHoleFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All</option>
                      {standbyFilterOptions.holeNames.map(hole => (
                        <option key={hole} value={hole}>{hole}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Activity Type</label>
                    <select 
                      value={standbyActivityTypeFilter} 
                      onChange={(e) => setStandbyActivityTypeFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Types</option>
                      {standbyFilterOptions.activityTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Activity</label>
                    <select 
                      value={standbyActivityFilter} 
                      onChange={(e) => setStandbyActivityFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Activities</option>
                      {standbyFilterOptions.activities.map(activity => (
                        <option key={activity} value={activity}>{activity}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </Card>

            {/* Data Display */}
            {activeTab === 'outliers' ? (
              <Card className="data-card">
                <div className="data-header">
                  <IconText icon={ICON.uiWarning} as="h2">
                    Activity Outliers ({filteredOutliers.length})
                  </IconText>
                </div>
                
                {filteredOutliers.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon"><SelkirkIcon name={ICON.uiChartAnalytics} size={48} decorative /></div>
                    <h3>No Outliers Found</h3>
                    <p>Try adjusting your filters to see more results.</p>
                  </div>
                ) : (
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Rig</th>
                          <th>Hole</th>
                          <th>Activity</th>
                          <th>Actual Hrs</th>
                          <th>Typical Hrs</th>
                          <th>Z-Score</th>
                          <th>Outlier Flag</th>
                          <th>Excess Cost</th>
                          <th>Risk</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOutliers.map((item, idx) => (
                          <tr key={idx} className={item.outlier_flag?.includes('Extreme') ? 'row-extreme' : ''}>
                            <td>{formatDate(item.report_date)}</td>
                            <td>{item.rig_name}</td>
                            <td className="cell-highlight">{item.hole_name}</td>
                            <td>
                              <div className="activity-cell">
                                <span className="activity-code">{item.activity_type}</span>
                                <span className="activity-desc">{item.activity}</span>
                              </div>
                            </td>
                            <td className="cell-number">{parseFloat(item.activity_hours || item.actual_hours || 0).toFixed(1)}</td>
                            <td className="cell-number">{parseFloat(item.typical_hours || 0).toFixed(1)}</td>
                            <td className="cell-number cell-z-score">{parseFloat(item.z_score || 0).toFixed(2)}σ</td>
                            <td>
                              <span className={`badge ${item.outlier_flag?.includes('Extreme') ? 'badge-danger' : 'badge-warning'}`}>
                                {item.outlier_flag}
                              </span>
                            </td>
                            <td className="cell-number cell-cost">{formatCurrency(item.excess_cost_if_billable || 0)}</td>
                            <td>
                              <span className={`risk-badge risk-${item.risk_score}`}>
                                {item.risk_score}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn-view"
                                onClick={() => setSelectedOutlier(item)}
                                title="View details"
                              >
                                <SelkirkIcon name={ICON.uiChartAnalytics} size={16} decorative />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ) : activeTab === 'standby' ? (
              <Card className="data-card">
                <div className="data-header">
                  <IconText icon={ICON.uiPause} as="h2">
                    Standby Detail ({filteredStandby.length})
                  </IconText>
                </div>
                
                {filteredStandby.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon"><SelkirkIcon name={ICON.uiPause} size={48} decorative /></div>
                    <h3>No Standby Records Found</h3>
                    <p>Try adjusting your filters to see more results.</p>
                  </div>
                ) : (
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Period</th>
                          <th>Rig</th>
                          <th>Hole</th>
                          <th>Activity</th>
                          <th>Standby Type</th>
                          <th>Hours</th>
                          <th>Cost</th>
                          <th>Billable</th>
                          <th>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStandby.map((item, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(item.report_date)}</td>
                            <td className="cell-period">{item.period_id}</td>
                            <td>{item.rig_name}</td>
                            <td className="cell-highlight">{item.hole_name}</td>
                            <td>
                              <div className="activity-cell">
                                <span className="activity-code">{item.activity_type}</span>
                                <span className="activity-desc">{item.activity}</span>
                              </div>
                            </td>
                            <td>{item.standby_type}</td>
                            <td className="cell-number">{parseFloat(item.activity_hours || item.standby_hours || 0).toFixed(1)}</td>
                            <td className="cell-number cell-cost">{formatCurrency(item.standby_cost || 0)}</td>
                            <td>
                              <span className={`badge ${item.billable === 'Y' || item.billable === true ? 'badge-success' : 'badge-secondary'}`}>
                                {item.billable === 'Y' || item.billable === true ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td className="cell-comments">
                              {item.daily_report_comments && item.daily_report_comments.length > 60 
                                ? item.daily_report_comments.substring(0, 60) + '...' 
                                : item.daily_report_comments}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ) : activeTab === 'invoices' ? (
              <Card className="data-card">
                <div className="data-header">
                  <IconText icon={ICON.uiDocument} as="h2">Invoice Reconciliation</IconText>
                </div>
                
                {groupedInvoices.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon"><SelkirkIcon name={ICON.uiDocument} size={48} decorative /></div>
                    <h3>No Invoice Data Found</h3>
                    <p>Try adjusting your filters to see more results.</p>
                  </div>
                ) : (
                  <div className="invoice-periods">
                    {groupedInvoices.map((period, periodIdx) => (
                      <div key={periodIdx} className="invoice-period-section">
                        <div className="period-header">
                          <div className="period-info">
                            <h3>{period.period_id}</h3>
                            <span className="period-dates">
                              {(() => {
                                const { start, end } = getDisplayPeriodRange(
                                  period.period_id,
                                  period.period_start_date,
                                  period.period_end_date
                                );
                                return `${formatDate(start)} - ${formatDate(end)}`;
                              })()}
                            </span>
                          </div>
                          <div className="period-total">
                            <span className="period-total-label">Period Total</span>
                            <span className="period-total-value">{formatCurrency(period.period_total)}</span>
                          </div>
                        </div>

                        {/* Animated Bar Chart */}
                        <div className="invoice-chart">
                          {period.categories.map((category, catIdx) => {
                            const percentage = (category.category_total / period.period_total) * 100;
                            const colors = {
                              'Labour': '#3b82f6',
                              'Drilling': '#10b981',
                              'Activity': '#f59e0b',
                              'Consumable': '#8b5cf6',
                              'Equipment': '#ec4899',
                              'Transport': '#06b6d4'
                            };
                            const color = colors[category.cost_category] || '#6b7280';
                            
                            return (
                              <div key={catIdx} className="chart-row" style={{ animationDelay: `${catIdx * 0.1}s` }}>
                                <div className="chart-label-section">
                                  <span className="chart-category-name">{category.cost_category}</span>
                                  <span className="chart-line-items">{category.line_items} items</span>
                                </div>
                                <div className="chart-bar-section">
                                  <div className="chart-bar-track">
                                    <div 
                                      className="chart-bar-fill"
                                      style={{ 
                                        width: `${percentage}%`,
                                        background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                                        animationDelay: `${catIdx * 0.1}s`
                                      }}
                                    >
                                      <span className="chart-bar-label">
                                        {formatCurrency(category.category_total)} ({percentage.toFixed(1)}%)
                                      </span>
                                    </div>
                                  </div>
                                  <div className="chart-percentage-badge" style={{ backgroundColor: color }}>
                                    {percentage.toFixed(0)}%
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Category Summary Table */}
                        <div className="category-summary-table">
                          <table className="summary-table">
                            <thead>
                              <tr>
                                <th>Category</th>
                                <th>Line Items</th>
                                <th>Quantity</th>
                                <th>Total</th>
                                <th>% of Period</th>
                              </tr>
                            </thead>
                            <tbody>
                              {period.categories.map((category, catIdx) => (
                                <tr key={catIdx}>
                                  <td className="cell-highlight">{category.cost_category}</td>
                                  <td className="cell-number">{category.line_items.toLocaleString()}</td>
                                  <td className="cell-number">{category.total_quantity.toFixed(1)}</td>
                                  <td className="cell-number cell-cost">{formatCurrency(category.category_total)}</td>
                                  <td className="cell-number">{category.pct_of_period_total.toFixed(2)}%</td>
                                </tr>
                              ))}
                              <tr className="summary-row">
                                <td><strong>Period Total</strong></td>
                                <td className="cell-number"><strong>{period.categories.reduce((sum, cat) => sum + cat.line_items, 0).toLocaleString()}</strong></td>
                                <td className="cell-number"><strong>{period.categories.reduce((sum, cat) => sum + cat.total_quantity, 0).toFixed(1)}</strong></td>
                                <td className="cell-number cell-cost"><strong>{formatCurrency(period.period_total)}</strong></td>
                                <td className="cell-number"><strong>100.00%</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : activeTab === 'timeline' ? (
              <Card className="data-card">
                <div className="data-header">
                  <IconText icon={ICON.uiChartAnalytics} as="h2">Cost & Penetration Timeline</IconText>
                </div>
                
                {timelineData.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon"><SelkirkIcon name={ICON.uiChartAnalytics} size={48} decorative /></div>
                    <h3>No Timeline Data Found</h3>
                    <p>Try adjusting your filters or date range to see more results.</p>
                  </div>
                ) : (
                  <div className="timeline-chart-container">
                    <ResponsiveContainer width="100%" height={500}>
                      <ComposedChart
                        data={timelineData}
                        margin={{ top: 20, right: 80, bottom: 20, left: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#9ca3af"
                          tick={{ fill: '#9ca3af', fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          yAxisId="left"
                          stroke="#9ca3af"
                          tick={{ fill: '#9ca3af' }}
                          label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                        />
                        <YAxis 
                          yAxisId="right"
                          orientation="right"
                          stroke="#eab308"
                          tick={{ fill: '#eab308' }}
                          label={{ value: 'Cost per Meter ($/m)', angle: 90, position: 'insideRight', fill: '#eab308' }}
                          domain={[0, 500]}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1f2937', 
                            border: '1px solid #374151',
                            borderRadius: '0.5rem',
                            color: '#f9fafb'
                          }}
                          formatter={(value, name) => {
                            // Format based on data type
                            if (!value && value !== 0) return ['N/A', name];
                            
                            const numValue = parseFloat(value);
                            if (isNaN(numValue)) return [value, name];
                            
                            // Cost values (anything with 'cost' in the name)
                            if (name.toLowerCase().includes('cost') || name.includes('$')) {
                              return [`$${numValue.toFixed(2)}`, name.replace('_cost', '')];
                            }
                            
                            // Meter values
                            if (name.toLowerCase().includes('meter') && !name.toLowerCase().includes('cost')) {
                              return [`${numValue.toFixed(1)}m`, name.replace('_meters', '')];
                            }
                            
                            // Cost per meter
                            if (name === 'costPerMeter' || name === 'Cost/Meter') {
                              return [`$${numValue.toFixed(2)}/m`, 'Cost/Meter'];
                            }
                            
                            return [numValue.toFixed(2), name];
                          }}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="rect"
                        />
                        
                        {/* Stacked cost bars by group */}
                        {timelineGroups.map((group, idx) => {
                          const colors = {
                            'Rig 9': '#3b82f6',
                            'Rig 10': '#f97316',
                            'Rig 11': '#22c55e',
                            'Rig 12': '#ec4899',
                            'Rig 13': '#8b5cf6',
                            'Rig 14': '#14b8a6',
                            'Rig 15': '#eab308',
                            'Rig 16': '#ef4444',
                            'Day': '#f59e0b',
                            'Night': '#3b82f6'
                          };
                          const color = colors[group] || '#6b7280';
                          
                          return (
                            <Bar
                              key={group}
                              yAxisId="left"
                              dataKey={`${group}_cost`}
                              stackId="cost"
                              name={group}
                              fill={color}
                              maxBarSize={60}
                            />
                          );
                        })}
                        
                        {/* Cost per meter line - PRIMARY FOCUS */}
                        {/* Transform data to clamp values above 500 for display */}
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey={(data) => {
                            // Clamp display value to 500 max, but keep original in payload
                            return Math.min(data.costPerMeter || 0, 500);
                          }}
                          name="Cost/Meter"
                          stroke="#eab308"
                          strokeWidth={4}
                          dot={(props) => {
                            const { cx, cy, payload, dataKey } = props;
                            const value = payload.costPerMeter;
                            
                            // If value exceeds 500, show at max height with red indicator and label
                            if (value > 500) {
                              // Calculate Y position for 500 on the chart
                              const maxY = cy; // This will be at the 500 position since we clamped it
                              
                              return (
                                <g>
                                  <circle cx={cx} cy={maxY} r={7} fill="#ef4444" stroke="#7f1d1d" strokeWidth={3} />
                                  <text 
                                    x={cx} 
                                    y={maxY - 12} 
                                    fill="#ef4444" 
                                    fontSize="12" 
                                    fontWeight="bold"
                                    textAnchor="middle"
                                  >
                                    ${value.toFixed(0)}
                                  </text>
                                </g>
                              );
                            }
                            
                            return <circle cx={cx} cy={cy} r={5} fill="#eab308" stroke="#854d0e" strokeWidth={2} />;
                          }}
                        />
                        
                        {/* Total meters line - subtle overlay */}
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="totalMeters"
                          name="Meters Drilled"
                          stroke="#10b981"
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          dot={false}
                          opacity={0.3}
                        />
                        
                        {/* Target cost/meter reference line */}
                        <ReferenceLine
                          yAxisId="right"
                          y={targetCostPerMeter}
                          stroke="#ef4444"
                          strokeDasharray="5 5"
                          strokeWidth={3}
                          label={{
                            value: `Target: $${targetCostPerMeter}/m`,
                            fill: '#ef4444',
                            fontSize: 14,
                            fontWeight: 'bold',
                            position: 'insideTopRight',
                            offset: 10
                          }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    
                    {/* Chart Legend Helper */}
                    <div className="chart-info">
                      <div className="chart-info-item">
                        <span className="chart-info-label"><IconText icon={ICON.uiTip} iconSize={14} as="span">Chart Guide:</IconText></span>
                        <span className="chart-info-text">
                          Bars show daily costs grouped by {timelineGroupBy === 'rig_name' ? 'rig' : 'shift'}.
                          Green line = meters drilled. Yellow dashed = cost per meter. Red dashed = your target.
                        </span>
                      </div>
                      {timelineStats.bestDay && (
                        <div className="chart-info-item">
                          <span className="chart-info-label"><IconText icon={ICON.uiTrophy} iconSize={14} as="span">Best Day:</IconText></span>
                          <span className="chart-info-text">
                            {formatDate(timelineStats.bestDay.date)} - ${timelineStats.bestDay.costPerMeter}/m
                          </span>
                        </div>
                      )}
                      {timelineStats.worstDay && (
                        <div className="chart-info-item">
                          <span className="chart-info-label"><IconText icon={ICON.uiWarning} iconSize={14} as="span">Highest Cost Day:</IconText></span>
                          <span className="chart-info-text">
                            {formatDate(timelineStats.worstDay.date)} - ${timelineStats.worstDay.costPerMeter}/m
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ) : null}

            {/* Outlier Visualization Modal */}
            {selectedOutlier && (
              <div className="modal-overlay" onClick={() => setSelectedOutlier(null)}>
                <Card className="modal-card" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <IconText icon={ICON.uiChartAnalytics} as="h2">Outlier Analysis</IconText>
                    <button className="modal-close" onClick={() => setSelectedOutlier(null)}>×</button>
                  </div>
                  
                  <div className="modal-content">
                    <div className="outlier-details">
                      <div className="detail-row">
                        <span className="detail-label">Hole:</span>
                        <span className="detail-value">{selectedOutlier.hole_name}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Activity:</span>
                        <span className="detail-value">{selectedOutlier.activity}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">{selectedOutlier.activity_type}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{formatDate(selectedOutlier.report_date)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Rig:</span>
                        <span className="detail-value">{selectedOutlier.rig_name}</span>
                      </div>
                    </div>

                    <div className="outlier-chart">
                      <h3>Time Comparison</h3>
                      <div className="chart-bars">
                        <div className="chart-bar-item">
                          <div className="chart-bar-label">Typical</div>
                          <div className="chart-bar-wrapper">
                            <div 
                              className="chart-bar chart-bar-typical" 
                              style={{ width: `${(parseFloat(selectedOutlier.typical_hours) / parseFloat(selectedOutlier.actual_hours)) * 100}%` }}
                            >
                              {parseFloat(selectedOutlier.typical_hours || 0).toFixed(1)}h
                            </div>
                          </div>
                        </div>
                        <div className="chart-bar-item">
                          <div className="chart-bar-label">Actual</div>
                          <div className="chart-bar-wrapper">
                            <div className="chart-bar chart-bar-actual" style={{ width: '100%' }}>
                              {parseFloat(selectedOutlier.actual_hours || 0).toFixed(1)}h
                            </div>
                          </div>
                        </div>
                        <div className="chart-bar-item">
                          <div className="chart-bar-label">P95</div>
                          <div className="chart-bar-wrapper">
                            <div 
                              className="chart-bar chart-bar-p95" 
                              style={{ width: `${(parseFloat(selectedOutlier.p95_typical_hours) / parseFloat(selectedOutlier.actual_hours)) * 100}%` }}
                            >
                              {parseFloat(selectedOutlier.p95_typical_hours || 0).toFixed(1)}h
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="outlier-stats-grid">
                      <div className="stat-box">
                        <div className="stat-box-label">Z-Score</div>
                        <div className="stat-box-value">{parseFloat(selectedOutlier.z_score || 0).toFixed(2)}σ</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-box-label">Excess Cost</div>
                        <div className="stat-box-value">{formatCurrency(selectedOutlier.excess_cost_if_billable || 0)}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-box-label">Risk Score</div>
                        <div className="stat-box-value risk-score-large">{selectedOutlier.risk_score}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-box-label">Times Performed</div>
                        <div className="stat-box-value">{selectedOutlier.times_activity_performed}</div>
                      </div>
                    </div>

                    {selectedOutlier.daily_report_comments && (
                      <div className="outlier-comments">
                        <h4>Daily Report Comments</h4>
                        <p>{selectedOutlier.daily_report_comments}</p>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default DrillingCosts;
